import React from 'react';
import { useTranslation } from 'react-i18next';
import {
  Box,
  Checkbox,
  Chip,
  FormControlLabel,
  IconButton,
  styled,
  Tooltip,
  Typography,
} from '@mui/material';
import PersonIcon from '@mui/icons-material/Person';
import AirportShuttleIcon from '@mui/icons-material/AirportShuttle';
import {
  DataGridPro,
  type GridCallbackDetails,
  type GridCellParams,
  type GridColDef,
  type GridPaginationModel,
} from '@mui/x-data-grid-pro';
import {
  TripLegStatus,
  tripStatusActiveList,
} from '../../../data/models/trip-leg-status.model';
import * as tripSlice from '../../../data/trip.slice';
import { setTripLegEvent } from '../../../data/trip.slice';
import * as referenceSlice from '../../../data/reference.slice';
import { useAppSelector } from '../../../hooks/useAppSelector';
import { useAppDispatch } from '../../../hooks/useAppDispatch';
import AssignDriverDialog from './AssignDriverDialog';
import AssignVehicleDialog from './AssignVehicleDialog';
import { useLoggedInProviderId } from '../../../hooks/useLoggedInProviderId';
import {
  SortModel,
  type TripSortModel,
} from '../../../data/models/sort-model.model';
import dateUtils from '../../../services/DateUtils';
import phoneUtils from '../../../services/phoneUtils';
import { TripLegSearchMenu } from '../../../data/requests/trip-legs';
import { TripLegSearchResponseItem } from '../../../data/models/trip-leg-search.model';
import { fetchPassengerCareLevelsAsync } from '../../../data/reference.slice';

const PREFIX = 'Trips';

export const classes = {
  container: `${PREFIX}-container`,
  content: `${PREFIX}-content`,
  filters: `${PREFIX}-filters`,
  grid: `${PREFIX}-grid`,
};

const StyledBox = styled(Box)(({ theme }) => ({
  [`&.${classes.container}`]: {
    flexGrow: 1,
    display: 'flex',
    flexDirection: 'column',
    margin: theme.spacing(2),
    gap: theme.spacing(2),
  },
  [`& .${classes.content}`]: {
    display: 'flex',
    flexDirection: 'column',
    gap: theme.spacing(2),
  },
  [`& .${classes.filters}`]: {
    display: 'flex',
    flexWrap: 'wrap',
    gap: theme.spacing(2),
  },
  [`& .${classes.grid}`]: {
    '&.MuiDataGrid-root .MuiDataGrid-columnHeader:focus, &.MuiDataGrid-root .MuiDataGrid-cell:focus':
      {
        outline: 'none',
      },
  },
}));

const initialSortModel: TripSortModel = {
  column: 'appointmentDate,pickUpDueTimeUTC',
  direction: 'asc,asc',
};

export default function Trips() {
  const { t } = useTranslation('common');
  const dispatch = useAppDispatch();
  const providerId = useLoggedInProviderId();

  const loadingTrips = useAppSelector(tripSlice.selectLoadingTripLegs);
  const providerTrips = useAppSelector(tripSlice.selectTripLegs);

  const tripLegEvent = useAppSelector(tripSlice.selectTripLegEvent);
  const timezones = useAppSelector(referenceSlice.selectTimeZones);
  const passengerCareLevels = useAppSelector(
    referenceSlice.selectPassengerCareLevels,
  );

  const [page, setPage] = React.useState(1);
  const [pageSize, setPageSize] = React.useState(10);
  const sortModel = initialSortModel;
  const [showAssigned, setShowAssigned] = React.useState(false);
  const [showUnassigned, setShowUnassigned] = React.useState(true);
  const [assignDialogOpen, setAssignDialogOpen] = React.useState(false);
  const [assignVehicleDialogOpen, setAssignVehicleDialogOpen] =
    React.useState(false);
  const [tripLegReferenceID, setTripLegReferenceID] = React.useState<string>();

  const isDriverAssigned =
    showAssigned && !showUnassigned
      ? true
      : !showAssigned && showUnassigned
      ? false
      : undefined;

  React.useEffect(() => {
    dispatch(fetchPassengerCareLevelsAsync());
  }, []);

  const fetchTrips = () => {
    if (providerId) {
      dispatch(
        tripSlice.fetchTripLegsSearchAsync({
          menu: TripLegSearchMenu.RC_TRIPS,
          page,
          pageSize,
          transportationProviderID: providerId,
          isDriverAssigned,
        }),
      );
    }
  };

  React.useEffect(() => {
    fetchTrips();
  }, [providerId, showAssigned, showUnassigned, page, pageSize, sortModel]);

  const assignDriverToTrip = (tripLeg: TripLegSearchResponseItem) => {
    setTripLegReferenceID(tripLeg.tripLegReferenceID);
    setAssignDialogOpen(true);
  };

  const assignVehicleToTrip = (tripLeg: TripLegSearchResponseItem) => {
    setTripLegReferenceID(tripLeg.tripLegReferenceID);
    setAssignVehicleDialogOpen(true);
  };

  const closeAssignDialog = (saved: boolean) => {
    setAssignDialogOpen(false);
    if (saved) {
      fetchTrips();
    }
  };

  const closeAssignVehicleDialog = (saved: boolean) => {
    setAssignVehicleDialogOpen(false);
    if (saved) {
      fetchTrips();
    }
  };

  const onPageChange = (pg: number) => {
    setPage(pg);
  };

  const onPageSizeChange = (pgSize: number) => {
    setPageSize(pgSize);
  };

  const paginationChange = (
    model: GridPaginationModel,
    _details: GridCallbackDetails,
  ) => {
    if (model.page !== providerTrips.currentPage - 1) {
      onPageChange(model.page + 1);
    }
    if (model.pageSize !== pageSize) {
      onPageSizeChange(model.pageSize);
    }
  };

  const renderTripID = (params: GridCellParams): React.ReactElement => {
    const { row } = params;
    return <>{`F1-${row.tripLegReferenceID}`}</>;
  };

  const renderPickupTime = (params: GridCellParams): React.ReactElement => {
    const { row } = params;
    if (row.isWillCall) {
      const tz = dateUtils.getTimeZoneIana(
        timezones,
        params.row.dropOffAddressTimeZoneID
          ? params.row.dropOffAddressTimeZoneID
          : params.row.pickUpAddressTimeZoneID,
      );
      return (
        <Box>
          <Typography>
            {dateUtils.formatAppointmentDate(params.row.appointmentDate, tz)}
          </Typography>
          <Typography variant="body2" textTransform="uppercase">
            {t('trips.willCall')}
          </Typography>
        </Box>
      );
    } else {
      const tz = dateUtils.getTimeZoneIana(
        timezones,
        params.row.pickUpAddressTimeZoneID,
      );
      return (
        <Box>
          <Typography>
            {dateUtils.formatPickupDate(params.row.pickUpDueTimeUTC, tz)}
          </Typography>
          <Typography>
            {dateUtils.formatPickupTime(params.row.pickUpDueTimeUTC, tz)}
          </Typography>
        </Box>
      );
    }
  };

  const renderPickupAddress = (params: GridCellParams): React.ReactElement => {
    const tripLeg: TripLegSearchResponseItem = params.row;
    return (
      <Box>
        <Typography>{`${tripLeg.pickUpAddressLine1}`}</Typography>
        <Typography>{`${tripLeg.pickUpCity}, ${tripLeg.pickUpStateCode} ${tripLeg.pickUpZipCode}`}</Typography>
      </Box>
    );
  };

  const renderDropoffAddress = (params: GridCellParams): React.ReactElement => {
    const tripLeg: TripLegSearchResponseItem = params.row;
    return (
      <Box>
        <Typography>{`${tripLeg.dropOffAddressLine1}`}</Typography>
        <Typography>{`${tripLeg.dropOffCity}, ${tripLeg.dropOffStateCode} ${tripLeg.dropOffZipCode}`}</Typography>
      </Box>
    );
  };

  const renderMember = (params: GridCellParams): React.ReactElement => {
    const {
      passengerFirstName,
      passengerLastName,
      pickUpPassengerPhoneNumber,
    } = params.row as TripLegSearchResponseItem;

    const fullName = [passengerFirstName, passengerLastName]
      .filter(Boolean)
      .join(' ')
      .trim();
    const formattedPhone = pickUpPassengerPhoneNumber
      ? phoneUtils.formatPhoneWithParens(pickUpPassengerPhoneNumber)
      : null;
    return (
      <Box>
        <Typography>{fullName}</Typography>
        {formattedPhone && (
          <Typography variant="body2" color="text.secondary">
            {formattedPhone}
          </Typography>
        )}
      </Box>
    );
  };

  const renderServiceLevel = (params: GridCellParams): React.ReactElement => {
    const tripLeg = params.row as TripLegSearchResponseItem;
    const primaryLabel = tripLeg.tripModeBC || '';
    const secondaryLabel =
      passengerCareLevels?.find(
        (careLevelObj) =>
          careLevelObj.passengerCareLevelBC === tripLeg.passengerCareLevelBC,
      )?.passengerCareLevelName || '';

    return (
      <Box>
        <Typography>{primaryLabel}</Typography>
        {secondaryLabel && (
          <Typography variant="body2" color="text.secondary">
            {secondaryLabel}
          </Typography>
        )}
      </Box>
    );
  };

  const renderStatus = (params: GridCellParams): React.ReactElement => {
    return <Chip label={params.row.tripLegStatusBC} />;
  };

  const columns: GridColDef[] = [
    {
      field: 'tripLegReferenceID',
      headerName: t('trips.tripId'),
      width: 100,
      sortable: false,
      renderCell: renderTripID,
    },
    {
      field: 'member',
      headerName: t('trips.member'),
      width: 180,
      sortable: false,
      renderCell: renderMember,
    },
    {
      field: 'serviceLevel',
      headerName: t('trips.serviceLevel'),
      width: 150,
      sortable: false,
      renderCell: renderServiceLevel,
    },
    {
      field: 'date',
      headerName: t('trips.pickupTime'),
      width: 180,
      sortable: false,
      renderCell: renderPickupTime,
    },
    {
      field: 'pickupAddress',
      headerName: t('trips.pickupAddress'),
      flex: 1,
      sortable: false,
      renderCell: renderPickupAddress,
    },
    {
      field: 'dropoffAddress',
      headerName: t('trips.dropoffAddress'),
      flex: 1,
      sortable: false,
      renderCell: renderDropoffAddress,
    },
    {
      field: 'driverName',
      headerName: t('trips.driver'),
      width: 175,
      sortable: false,
    },
    {
      field: 'tripStatus',
      headerName: t('trips.status'),
      width: 140,
      sortable: false,
      renderCell: renderStatus,
    },
    {
      field: 'actions',
      headerName: ' ',
      width: 120,
      sortable: false,
      renderCell: (params: GridCellParams) => (
        <>
          <Tooltip title={t('trips.assignDriver')}>
            <span>
              <IconButton
                data-testid="assign-driver"
                disabled={
                  params.row.tripLegStatusBC !==
                  TripLegStatus.Accepted.toString().toLocaleUpperCase()
                }
                onClick={(_evt) => assignDriverToTrip(params.row)}
                size="medium"
                color="primary">
                <PersonIcon />
              </IconButton>
            </span>
          </Tooltip>
          <Tooltip title={t('trips.assignVehicle')}>
            <span>
              <IconButton
                data-testid="assign-vehicle"
                disabled={
                  params.row.tripLegStatusBC !==
                  TripLegStatus.Accepted.toString().toLocaleUpperCase()
                }
                onClick={(_evt) => assignVehicleToTrip(params.row)}
                size="medium"
                color="primary">
                <AirportShuttleIcon />
              </IconButton>
            </span>
          </Tooltip>
        </>
      ),
    },
  ];

  const changeUnassigned = (evt: React.ChangeEvent<HTMLInputElement>) => {
    setShowUnassigned(evt.target.checked);
  };

  const changeAssigned = (evt: React.ChangeEvent<HTMLInputElement>) => {
    setShowAssigned(evt.target.checked);
  };

  React.useEffect(() => {
    if (tripLegEvent) {
      fetchTrips();
      dispatch(setTripLegEvent(null));
    }
  }, [tripLegEvent]);

  return (
    <StyledBox className={classes.container}>
      <Typography variant="h4">{t('common.trips')}</Typography>
      <Box className={classes.content}>
        <Box className={classes.filters}>
          <FormControlLabel
            control={
              <Checkbox checked={showUnassigned} onChange={changeUnassigned} />
            }
            label={t('trips.showUnassigned')}
          />
          <FormControlLabel
            control={
              <Checkbox checked={showAssigned} onChange={changeAssigned} />
            }
            label={t('trips.showAssigned')}
          />
        </Box>
        <DataGridPro
          getRowId={(row) => row.tripLegReferenceID}
          rows={providerTrips.items}
          columns={columns}
          pagination={true}
          pageSizeOptions={[10, 20, 50]}
          paginationModel={{ page: providerTrips.currentPage - 1, pageSize }}
          rowCount={providerTrips.itemCount}
          rowReordering={false}
          autoHeight={true}
          rowSelection={false}
          className={classes.grid}
          checkboxSelection={false}
          disableColumnMenu={true}
          disableColumnSelector={true}
          disableRowSelectionOnClick={true}
          hideFooter={false}
          loading={loadingTrips}
          paginationMode="server"
          sortingMode="server"
          onPaginationModelChange={paginationChange}
        />
      </Box>
      {providerId && tripLegReferenceID && (
        <>
          <AssignDriverDialog
            open={assignDialogOpen}
            providerId={providerId}
            tripLegReferenceID={tripLegReferenceID}
            close={closeAssignDialog}
          />
          <AssignVehicleDialog
            open={assignVehicleDialogOpen}
            providerId={providerId}
            tripLegReferenceID={tripLegReferenceID}
            close={closeAssignVehicleDialog}
          />
        </>
      )}
    </StyledBox>
  );
}
